import apiClient, { getAuthToken } from '@/lib/api/apiClient'
import { getBrowserApiBaseUrl } from '@/config/env'
import { API_ENDPOINTS } from '@/lib/api/config'
import { throwServiceError } from './serviceHelpers'
import { normalizePaymentConfigResponse } from '@/lib/utils/paymentCalculations'

/** Note attachment on note row (Angular `NoteListComponent.deleteFile`). */
export async function deleteAttachmentDefault(fileId: string | number): Promise<void> {
  try {
    await apiClient.delete(`/api/attachments/default/${fileId}`)
  } catch (error) {
    throwServiceError(error, 'Failed to remove attachment.')
  }
}

/** Payment adjustment dialog → `/api/payment_refund/:paymentLogId` (Angular payment-refund flow). */
export async function submitPaymentRefundAdjustment(
  paymentLogId: string | number,
  payload: Record<string, unknown>,
): Promise<void> {
  try {
    const body: Record<string, unknown> = {}
    Object.entries(payload).forEach(([key, value]) => {
      if (
        value === undefined ||
        value === null ||
        value === '' ||
        key === 'payment_id'
      ) {
        return
      }
      if (key === 'is_covid') {
        if (value) body.covid_19 = value
        return
      }
      if (key === 'covid_19' && !value) return
      body[key] = value
    })
    await apiClient.post(`/api/payment_refund/${paymentLogId}`, body)
  } catch (error) {
    throwServiceError(error, 'Failed to submit payment adjustment.')
  }
}

/** Default Invoice submit → `/api/default_invoice/:applicantId` (Angular `default_invoice`, drops txn_number). */
export async function submitDefaultInvoice(
  applicantId: string | number,
  payload: Record<string, unknown>,
): Promise<void> {
  try {
    const body: Record<string, unknown> = {}
    Object.entries(payload).forEach(([key, value]) => {
      if (
        value === undefined ||
        value === null ||
        value === '' ||
        key === 'payment_id' ||
        key === 'txn_number'
      ) {
        return
      }
      if (key === 'is_covid') {
        if (value) body.covid_19 = value
        return
      }
      if (key === 'covid_19' && !value) return
      body[key] = value
    })
    await apiClient.post(`/api/default_invoice/${applicantId}`, body)
  } catch (error) {
    throwServiceError(error, 'Failed to submit default invoice.')
  }
}

/** Unfreeze payment for applicant. */
export async function unfreezePayment(applicantId: string | number): Promise<void> {
  try {
    await apiClient.post(API_ENDPOINTS.UNFREEZE_PAYMENT(applicantId), {})
  } catch (error) {
    throwServiceError(error, 'Failed to unfreeze payment.')
  }
}

/** Name override — POST /api/name_override/:applicantId (Angular saveNameOverride). */
export async function saveNameOverride(
  applicantId: string | number,
  payment_name_override: 'YES' | 'NO',
  payment_name_override_reason: string,
): Promise<void> {
  try {
    await apiClient.post(`/api/name_override/${applicantId}`, {
      payment_name_override,
      payment_name_override_reason: payment_name_override_reason.trim(),
    })
  } catch (error) {
    throwServiceError(error, 'Failed to save name override.')
  }
}

/** Chargeback packet download URL with access_token (Angular downloadChargeback). */
export function buildChargebackPacketDownloadUrl(
  applicantId: string | number,
): string | null {
  const token = getAuthToken()
  if (!token) return null
  const base = getBrowserApiBaseUrl().replace(/\/+$/, '')
  const id = encodeURIComponent(String(applicantId))
  return `${base}/api/download_chargeback_packet/${id}?access_token=${encodeURIComponent(token)}`
}

/** Angular parity: `window.open(url, '_blank')` for chargeback packet download. */
export function openChargebackPacketDownload(applicantId: string | number): void {
  const url = buildChargebackPacketDownloadUrl(applicantId)
  if (!url) {
    throw new Error('Authentication required to download chargeback packet.')
  }
  window.open(url, '_blank', 'noopener,noreferrer')
}

/** Download chargeback packet blob — prefer openChargebackPacketDownload for UI. */
export async function downloadChargebackPacket(applicantId: string | number): Promise<Blob> {
  const url = buildChargebackPacketDownloadUrl(applicantId)
  if (!url) {
    throw new Error('Authentication required to download chargeback packet.')
  }
  try {
    const response = await fetch(url)
    if (!response.ok) {
      throw new Error(`Download failed with status ${response.status}`)
    }
    return await response.blob()
  } catch (error) {
    throwServiceError(error, 'Failed to download chargeback packet.')
  }
}

export async function submitPaymentFailedUnfreeze(
  applicantId: string | number,
  body: { unfreeze_reason: string },
): Promise<void> {
  try {
    await apiClient.post(`/api/payment_failed/${applicantId}`, body)
  } catch (error) {
    throwServiceError(error, 'Failed to submit payment unfreeze.')
  }
}

export async function postExternalPayment(
  applicantId: string | number,
  body: Record<string, unknown>,
): Promise<void> {
  try {
    await apiClient.post(`/api/payment/external/${applicantId}`, body)
  } catch (error) {
    throwServiceError(error, 'Failed to record external payment.')
  }
}

export async function postExternalRefund(
  applicantId: string | number,
  body: Record<string, unknown>,
): Promise<void> {
  try {
    await apiClient.post(`/api/payment/external_refund/${applicantId}`, body)
  } catch (error) {
    throwServiceError(error, 'Failed to record external refund.')
  }
}

export async function getPaymentConfig(): Promise<Record<string, Record<string, unknown>>> {
  try {
    const response = await apiClient.get<Record<string, Record<string, unknown>>>(
      '/api/config/payments',
    )
    return normalizePaymentConfigResponse(response.data) as Record<
      string,
      Record<string, unknown>
    >
  } catch (error) {
    throwServiceError(error, 'Failed to load payment config.')
  }
}
